import CloseIcon from '@mui/icons-material/Close';
import { DialogActions, Box, Button, IconButton, Typography, useTheme } from '@mui/material';
import { noop } from 'lodash';
import { useTranslation } from 'next-i18next';
import { login, register } from 'src/helpers/auth';

import FontAwesomeIcon from '@components/atoms/fontAwesomeIcon';
import { useCssClasses } from '@components/share/modal/authorizeModal/style';
import BaseModalWrapper from '@components/share/modal/baseModalWrapper';

type AuthorizeModalProps = {
  open?: boolean;
  onClose: typeof noop;
  currentUrl?: string;
  /**
   * Trigger at handleClickLogin and onClickRegister
   * when click login or register
   */
  onAuthorize?: typeof noop;
};

const AuthorizeModal = ({ open, onClose, currentUrl = '', onAuthorize }: AuthorizeModalProps) => {
  const { t } = useTranslation('common');
  const theme = useTheme();
  const classes = useCssClasses();

  const handleClickLogin = () => {
    onAuthorize?.();
    login(currentUrl);
  };

  const handleClickRegister = () => {
    onAuthorize?.();
    register();
  };

  return (
    <BaseModalWrapper
      open={open}
      onClose={onClose}
      maxWidth={560}
      paperStyle={{
        width: { md: 450, xs: 325 },
        height: { md: 248, xs: 352 },
        top: { md: '80px', xs: '48px' },
        position: 'absolute',
        pt: 3,
        pb: 2,
      }}
      disabledBackDrop
      hideCloseButton
      keepMounted={false}
    >
      <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </Box>
      <Box display="flex" alignItems="center" flexDirection="column" sx={{ gap: 3 }}>
        <FontAwesomeIcon
          style={{ height: 48, width: 48, fontSize: '48px' }}
          color={theme.palette.grey['500']}
          icon="fa-light fa-circle-exclamation"
        />
        <Typography color={theme.palette.textV2.primary} variant="h4" align="center">
          {t('login.modal.title')}
        </Typography>
      </Box>
      <Box>
        <Typography
          variant="body1lanna"
          align="center"
          display="flex"
          justifyContent="center"
          sx={{ padding: '16px 24px', flexDirection: { md: 'row', xs: 'column' } }}
          className={classes.loginDescription}
        >
          <span>{t('login.modal.subtitle1')}</span>
          <span>{t('login.modal.subtitle2')}</span>
        </Typography>
      </Box>
      <DialogActions
        disableSpacing
        sx={{
          px: 1,
          display: 'flex',
          justifyContent: 'center',
          flexDirection: { xs: 'column-reverse', md: 'row' },
          gap: { md: 3, xs: 2 },
        }}
      >
        <Button
          onClick={handleClickRegister}
          sx={{ width: { md: '189px', xs: '277px' } }}
          variant="outlined"
          color="primary"
        >
          {t('register.modal.button')}
        </Button>
        <Button
          onClick={handleClickLogin}
          sx={{ width: { md: '189px', xs: '277px' } }}
          variant="contained"
          color="primary"
        >
          {t('login.modal.button')}
        </Button>
      </DialogActions>
    </BaseModalWrapper>
  );
};

export default AuthorizeModal;
